import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getPOKItems } from '../../../redux/actions';
import CollapsibleSection from '../../common/collapsibleSection';
import GenericOverlayDialog from '../../dialogs/genericOverlayDialog';

const PartsNetInstallForm = forwardRef((props, ref) => {
	const [items, setItems] = useState([]);
	const [showInvalidRequestedQuantityDialog, setShowInvalidRequestedQuantityDialog] = useState(false);

	const { user } = useSelector(({ user }) => ({ user }));
	const dispatch = useDispatch();

	const { selectedAccount, selectedPriceSource } = user;

	useImperativeHandle(ref, () => ({
		getItems: () => {
			let itemsToAdd = [];
			let isQuantityError = false;

			items.forEach(item => {
				if (item.isSelected) {
					if (!isNaN(item.requestedQuantity) && item.requestedQuantity > 0 && item.requestedQuantity % item.itemDetails.pimData.geego_pok_ord_qty === 0) {
						itemsToAdd.push(item);
					} else {
						isQuantityError = true;
					}
				}
			});

			if (isQuantityError) {
				setShowInvalidRequestedQuantityDialog(true);
			}

			return {
				isQuantityError,
				items: itemsToAdd
			};
		}
	}));

	useEffect(() => {
		if ((selectedAccount, selectedPriceSource)) {
			const agreementNumber = selectedPriceSource.AGREEMENT_NUMBER;

			const fetchInstallItems = () => {
				dispatch(
					getPOKItems('requestType=pokItems', true, pokResponse => {
						if (pokResponse.status && pokResponse.responseObject) {
							const pokItemNumbers = pokResponse.responseObject;
							props.getItemDetails(
								{
									items: pokItemNumbers.map(itemNumber => ({ itemNumber })),
									agreementNumber
								},
								data => {
									const items = data.items
										.filter(
											item =>
												!item.isInvalid &&
												!item.isNLA &&
												!item.isRestricted &&
												item.isPOKInstall &&
												item.pricingData &&
												item.pricingData.records &&
												item.pricingData.records.some(pricingRecord => pricingRecord.unitPrice)
										)
										.toSorted((a, b) => a.itemNumber.localeCompare(b.itemNumber))
										.map(item => ({
											id: item.id,
											itemNumber: item.itemNumber,
											itemDescription: item.pimData.item_description,
											originalItemNumber: item.originalItemNumber,
											agreementNumber,
											requestedQuantity: '',
											isPOKInstall: item.isPOKInstall,
											isVolumeDiscountEligible: item.isVolumeDiscountEligible,
											itemType: item.itemType,
											itemDetails: {
												availabilityData: item.availabilityData,
												orderabilityData: item.orderabilityData,
												pricingData: item.pricingData,
												pimData: item.pimData
											}
										}));
									setItems(items);
								},
								{
									searchContext: 'PNET',
									include: {
										pim: true,
										orderability: true,
										pricing: true
									},
									skipPOKCheck: true,
									showLoader: true
								}
							);
						}
					})
				);
			};

			fetchInstallItems();
		}
	}, [selectedAccount, selectedPriceSource]);

	const handleDialog = (dialog, show) => {
		if (dialog === 'InvalidRequestedQuantityDialog') {
			setShowInvalidRequestedQuantityDialog(show);
		}
	};

	const toggleIndividualSelection = (id, event) => {
		const isSelected = event.target.checked;

		setItems(items => items.map(item => (item.id === id ? { ...item, isSelected } : item)));
	};

	const toggleGroupSelection = event => {
		const isSelected = event.target.checked;
		setItems(items => items.map(item => ({ ...item, isSelected })));
	};

	const handleRequestedQuantityChange = (id, event) => {
		const requestedQuantity = Number(event.target.value).toString();

		setItems(items => items.map(item => (item.id === id ? { ...item, requestedQuantity, isSelected: item.isSelected || requestedQuantity > 0 } : item)));
	};

	return (
		<React.Fragment>
			<CollapsibleSection title="Install Form" expand={items.length}>
				{items.length > 0 && (
					<form>
						<div className="pNetQOInstallTableWrapper">
							<table className="pNetQOInstallTable dataTable font12 fullWidth mt-0">
								<thead>
									<tr>
										<th className="textCenter">
											<label htmlFor="select-all-toggle" style={{ display: 'block', width: 'max-content' }}>
												<input
													id="select-all-toggle"
													type="checkbox"
													checked={items.filter(product => product.isSelected).length === items.length}
													onChange={toggleGroupSelection}
												/>
												All
											</label>
										</th>
										<th className="textCenter">Item</th>
										<th className="textCenter">Item Description</th>
										<th className="textCenter" style={{ width: '12rem' }}>
											<span className="show">Requested Qty</span>
											<i>(in multiples of Box Qty)</i>
										</th>
										<th className="textCenter">Box Qty</th>
										<th className="textCenter">UOM</th>
										<th className="textCenter">Book Price</th>
										<th className="textCenter">Sales Agreement</th>
									</tr>
								</thead>
								<tbody className="text-center">
									{items.map(item => {
										const boxQuantity = item.itemDetails.pimData && item.itemDetails.pimData.geego_pok_ord_qty;
										const unitOfMeasure =
											item.itemDetails.pimData &&
											(item.itemDetails.pimData.primary_unit_of_measure === 'EACH' ? 'EA' : item.itemDetails.pimData.primary_unit_of_measure);
										const bookPrice = item.itemDetails.pricingData && item.itemDetails.pricingData.records[0].unitPrice;

										return (
											<tr key={item.id}>
												<td>
													<input type="checkbox" checked={item.isSelected || false} onChange={toggleIndividualSelection.bind(this, item.id)} />
												</td>
												<td className="text-left">{item.itemNumber}</td>
												<td>{item.itemDescription}</td>
												<td className="quantityInput">
													<input
														type="number"
														className={`short text-center${
															item.itemNumber &&
															item.isSelected &&
															(!item.requestedQuantity || item.requestedQuantity <= 0 || item.requestedQuantity % boxQuantity !== 0)
																? ' error'
																: ''
														}`}
														min={0}
														value={item.requestedQuantity.toString()}
														onChange={handleRequestedQuantityChange.bind(this, item.id)}
													/>
												</td>
												<td>{boxQuantity}</td>
												<td>{unitOfMeasure}</td>
												<td>{props.formatMoney(bookPrice, 0, '.', ',', false, '$')}</td>
												<td>
													<select value={item.agreementNumber} onChange={() => {}}>
														{props.salesAgreements.map(({ AGREEMENT_NUMBER, AGREEMENT_NAME }) => (
															<option key={AGREEMENT_NUMBER}>{AGREEMENT_NAME}</option>
														))}
													</select>
												</td>
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					</form>
				)}
			</CollapsibleSection>
			{showInvalidRequestedQuantityDialog ? (
				<GenericOverlayDialog
					buttons="ok"
					genericDialogHeading="Invalid Quantity"
					genericDialogText="POK quantity must be in multiples of box quantity."
					flag="InvalidRequestedQuantityDialog"
					handleOk={handleDialog.bind(this, 'InvalidRequestedQuantityDialog', false)}
					showDialog
					defaultTop
				/>
			) : null}
		</React.Fragment>
	);
});

export default PartsNetInstallForm;



// WEBPACK FOOTER //
// ./src/App/components/PostLogin/Order/partsNetInstallForm.jsx